import invariant from 'invariant'
import * as React from 'react'

import { cn } from '~/styled'

import { useBannerContext } from '../BannerContext'
import { BannerRegularButton } from './BannerRegularButton'

const MAX_ALLOWED_CHILDREN = 2

const toGap = {
  sm: 'gap-2',
  md: 'gap-4'
}

const toDirection = {
  sm: 'flex-col',
  md: 'flex-row'
}

export const BannerRegularActions = ({
  children,
  className,
  ...props
}: React.PropsWithChildren<React.ComponentProps<'div'>>) => {
  const { size } = useBannerContext()

  const gap = React.useMemo(() => {
    if (typeof size === 'string') {
      return toGap[size]
    }
    return 'gap-2 md:gap-4'
  }, [size])

  const direction = React.useMemo(() => {
    if (typeof size === 'string') {
      return toDirection[size]
    }
    return 'flex-col md:flex-row'
  }, [size])

  invariant(
    React.Children.count(children) <= MAX_ALLOWED_CHILDREN,
    `A maximum of ${MAX_ALLOWED_CHILDREN} ${BannerRegularButton.displayName} component(s) are permitted as children of ${BannerRegularActions.displayName}`
  )

  return (
    <div className={cn('flex', gap, direction, className)} {...props}>
      {React.Children.map(children, (child, index) => {
        // if child is undefined or null, React.isValidElement returns false and hence error is thrown.
        // This line will prevent that from happening
        if (child == null) return child

        if (!React.isValidElement(child)) {
          throw new Error(
            `Child passed to ${BannerRegularActions.displayName} is not a valid element`
          )
        }

        invariant(
          child.type === BannerRegularButton,
          `Children of type ${child?.type} aren't permitted. Only an ${BannerRegularButton.displayName} component is allowed in ${BannerRegularActions.displayName}`
        )

        const propsToInject: Partial<
          React.ComponentProps<typeof BannerRegularButton>
        > = {
          // Override button appearance - make the second button outlined
          appearance: index > 0 ? 'outline' : undefined
        }

        return React.cloneElement(
          child as React.ReactElement<
            React.ComponentProps<typeof BannerRegularButton>
          >,
          propsToInject
        )
      })}
    </div>
  )
}

BannerRegularActions.displayName = 'BannerRegularActions'
